import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, ChefHat, MessageCircle, Mail } from 'lucide-react';
const faqs = [{
  q: '¿Sirve si nunca usé inteligencia artificial?',
  a: 'Sí. El Tomo 1 arranca desde cero con configuración paso a paso. El Bono 1 te tiene operativo en menos de 60 minutos.'
}, {
  q: '¿Cómo recibo el material?',
  a: 'Inmediatamente después del pago recibís un email con acceso a todos los PDFs. El acceso es permanente.'
}, {
  q: '¿Funciona si soy nutricionista, entrenador o las dos cosas?',
  a: 'Sí. Está diseñado para cualquier profesional del sector salud y fitness que quiera crear y vender planes nutricionales online.'
}, {
  q: '¿Qué lo diferencia de un curso de YouTube?',
  a: 'Los cursos de YouTube son fragmentados y sin sistema. Este kit es un protocolo completo de principio a fin con prompts listos, plantillas y scripts que usás hoy.'
}, {
  q: '¿Tiene garantía real?',
  a: 'Sí. 7 días de garantía total. Si no es lo que esperabas, te devolvemos el 100% sin condiciones.'
}, {
  q: '¿Necesito herramientas pagas?',
  a: 'No para empezar. Funciona con ChatGPT gratuito, Canva gratuito y Google Drive gratuito.'
}];
export function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  return <section className="py-16 bg-petroleum/30 border-y border-white/5 relative overflow-hidden" data-id="element-192">
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] bg-electric/5 blur-[120px] rounded-full pointer-events-none" data-id="element-193"></div>

      <div className="container mx-auto px-4 relative z-10" data-id="element-194">
        {/* Framed Header */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="relative max-w-3xl mx-auto mb-12" data-id="element-195">
          <div className="absolute -inset-1 bg-gradient-to-r from-electric via-energy to-electric rounded-3xl blur-xl opacity-30 animate-pulse" data-id="element-196"></div>
          <div className="relative bg-gradient-to-br from-graphite via-petroleum to-graphite border-2 border-electric rounded-3xl p-8 md:p-10 text-center overflow-hidden shadow-2xl shadow-electric/20" data-id="element-197">
            {/* Decorative chef hat flourish in background */}
            <div className="absolute -top-16 -right-16 opacity-10 pointer-events-none rotate-12" data-id="element-198">
              <ChefHat className="w-48 h-48 text-electric" data-id="element-199" />
            </div>
            <div className="absolute -bottom-16 -left-16 opacity-10 pointer-events-none -rotate-12" data-id="element-200">
              <ChefHat className="w-48 h-48 text-electric" data-id="element-201" />
            </div>

            {/* Organic dot pattern background */}
            <div className="absolute inset-0 opacity-[0.18] pointer-events-none" style={{
            backgroundImage: 'radial-gradient(circle, rgba(125,255,47,0.35) 1.2px, transparent 1.5px)',
            backgroundSize: '18px 18px'
          }} data-id="element-202"></div>
            <div className="absolute top-0 left-0 w-14 h-14 border-t-4 border-l-4 border-electric rounded-tl-3xl pointer-events-none" data-id="element-203"></div>
            <div className="absolute top-0 right-0 w-14 h-14 border-t-4 border-r-4 border-electric rounded-tr-3xl pointer-events-none" data-id="element-204"></div>
            <div className="absolute bottom-0 left-0 w-14 h-14 border-b-4 border-l-4 border-electric rounded-bl-3xl pointer-events-none" data-id="element-205"></div>
            <div className="absolute bottom-0 right-0 w-14 h-14 border-b-4 border-r-4 border-electric rounded-br-3xl pointer-events-none" data-id="element-206"></div>

            <div className="relative z-10" data-id="element-207">
              <div className="inline-flex items-center gap-2 bg-electric text-graphite px-4 py-1.5 rounded-full text-xs font-extrabold uppercase tracking-wider mb-5 shadow-lg shadow-electric/40" data-id="element-208">
                <ChefHat className="w-3.5 h-3.5" strokeWidth={2.5} data-id="element-209" />
                Preguntas Frecuentes
              </div>
              <h2 className="text-2xl md:text-4xl font-extrabold leading-tight" data-id="element-210">
                <span className="text-white" data-id="element-211">Lo que más </span>
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-electric via-energy to-electric drop-shadow-[0_0_12px_rgba(125,255,47,0.4)]" data-id="element-212">
                  nos preguntan
                </span>
              </h2>
              <div className="mt-4 mx-auto h-px w-32 bg-gradient-to-r from-transparent via-electric to-transparent opacity-70" data-id="element-213"></div>
              <p className="mt-4 text-sm text-textsecondary" data-id="element-214">
                Todo lo que necesitás saber antes de empezar
              </p>
            </div>
          </div>
        </motion.div>

        {/* FAQ List */}
        <div className="max-w-3xl mx-auto space-y-3" data-id="element-215">
          {faqs.map((faq, i) => {
          const isOpen = openIndex === i;
          return <motion.div key={i} initial={{
            opacity: 0,
            y: 10
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.04
          }} className={`relative bg-gradient-to-br from-graphite to-petroleum/60 border rounded-2xl overflow-hidden transition-all duration-300 ${isOpen ? 'border-electric/50 shadow-[0_0_25px_rgba(125,255,47,0.15)]' : 'border-white/10 hover:border-electric/25'}`} data-id="element-216">
                {/* Left accent bar when open */}
                {isOpen && <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-electric via-energy to-electric" data-id="element-217"></div>}

                <button onClick={() => setOpenIndex(isOpen ? null : i)} className="w-full flex items-center justify-between gap-4 p-5 text-left group" aria-expanded={isOpen} data-id="element-218">
                  <div className="flex items-start gap-4 flex-1 min-w-0" data-id="element-219">
                    <span className={`flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center font-bold text-xs transition-all ${isOpen ? 'bg-electric text-graphite shadow-md shadow-electric/30' : 'bg-petroleum text-electric border border-electric/30 group-hover:bg-electric/10'}`} data-id="element-220">
                      {String(i + 1).padStart(2, '0')}
                    </span>
                    <span className={`font-semibold leading-snug pt-1 transition-colors ${isOpen ? 'text-white' : 'text-white/90'}`} data-id="element-221">
                      {faq.q}
                    </span>
                  </div>
                  <motion.div animate={{
                rotate: isOpen ? 180 : 0
              }} transition={{
                duration: 0.25
              }} className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-colors ${isOpen ? 'bg-electric/15 text-electric' : 'bg-white/5 text-textsecondary group-hover:bg-white/10'}`} data-id="element-222">
                    <ChevronDown className="w-4 h-4" strokeWidth={2.5} data-id="element-223" />
                  </motion.div>
                </button>

                <AnimatePresence initial={false} data-id="element-224">
                  {isOpen && <motion.div key="content" initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.25,
                ease: 'easeOut'
              }} className="overflow-hidden" data-id="element-225">
                      <div className="px-5 pb-5 pl-[68px]" data-id="element-226">
                        <div className="pt-4 border-t border-white/5 text-textsecondary leading-relaxed text-sm" data-id="element-227">
                          {faq.a}
                        </div>
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </motion.div>;
        })}
        </div>

        {/* Help footer card */}
        <motion.div initial={{
        opacity: 0,
        y: 15
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="max-w-3xl mx-auto mt-8" data-id="element-228">
          <div className="bg-gradient-to-r from-petroleum via-graphite to-petroleum border border-electric/20 rounded-2xl p-5 flex flex-col sm:flex-row items-center justify-between gap-4" data-id="element-229">
            <div className="flex items-center gap-3" data-id="element-230">
              <div className="w-11 h-11 rounded-xl bg-electric/15 border border-electric/30 flex items-center justify-center flex-shrink-0" data-id="element-231">
                <MessageCircle className="w-5 h-5 text-electric" data-id="element-232" />
              </div>
              <div data-id="element-233">
                <p className="text-white font-bold text-sm" data-id="element-234">
                  ¿Te quedó otra duda?
                </p>
                <p className="text-textsecondary text-xs" data-id="element-235">
                  Te respondemos en menos de 24 horas
                </p>
              </div>
            </div>
            <a href="mailto:soportebibliotecasdigitales@gmail.com" className="inline-flex items-center gap-2 bg-electric hover:bg-electric/90 text-graphite font-bold px-5 py-2.5 rounded-xl text-sm transition-all hover:shadow-lg hover:shadow-electric/30" data-id="element-236">
              <Mail className="w-4 h-4" data-id="element-237" />
              Escribinos
            </a>
          </div>
        </motion.div>
      </div>
    </section>;
}